import { BalancesType, OrderDirection } from '@bitly/sdk/types'
import { createSelector } from '@reduxjs/toolkit'

import { DEFAULT_NETWORK_ID } from 'constants/defaults'
import { selectMarkets, selectOpenOrders, selectTokens } from 'state/exchange/selectors'
import { selectOraclePrices } from 'state/prices/selectors'
import type { RootState } from 'state/store'
import { FetchStatus } from 'state/types'
import { getPriceByOraclePrice } from 'utils/prices'

export const selectIsWalletConnected = createSelector(
	(state: RootState) => state.wallet.walletAddress,
	(walletAddress) => !!walletAddress
)

export const selectBalanceInWallet = (state: RootState) => state.wallet.balanceInWallet

export const selectBalanceInBank = (state: RootState) => state.wallet.balanceInBank

export const selectAllowances = (state: RootState) => state.wallet.allowances

export const selectIsDeposited = createSelector(
	(state: RootState) => state.wallet.writeStatuses.deposit,
	(depositStatus) => depositStatus === FetchStatus.Success || depositStatus === FetchStatus.Error
)

export const selectIsWithdrawed = createSelector(
	(state: RootState) => state.wallet.writeStatuses.withdraw,
	(withdrawStatus) => withdrawStatus === FetchStatus.Success || withdrawStatus === FetchStatus.Error
)

export const selectIsApproving = createSelector(
	(state: RootState) => state.wallet.writeStatuses.approve,
	(approveStatus) => approveStatus === FetchStatus.Loading
)

export const selectIsDepositing = createSelector(
	(state: RootState) => state.wallet.writeStatuses.deposit,
	(depositStatus) => depositStatus === FetchStatus.Loading
)

export const selectIsWithdrawing = createSelector(
	(state: RootState) => state.wallet.writeStatuses.withdraw,
	(withdrawStatus) => withdrawStatus === FetchStatus.Loading
)

export const selectBalancesLoading = 
	(state: RootState) => state.wallet.queryStatuses.balances.status == FetchStatus.Loading

export const selectTotalBalance = createSelector(
	(state: RootState) => state.wallet.balanceInBank,
	(state: RootState) => state.prices.onChainPrices,
	selectTokens,
	(state: RootState) => state.wallet.networkId,
	selectOraclePrices,
	(balanceInBank, prices, tokenInfo, networkId, oraclePrices) => {
		// Calculate total balance currently
		let currentBalance: number = 0
		for (const [token, balance] of Object.entries(balanceInBank)) {
			const network = networkId ?? DEFAULT_NETWORK_ID
			const info = tokenInfo.find(t=>t.address===token)
			if (info !== undefined) {
				const price = getPriceByOraclePrice(token, tokenInfo, network, prices, oraclePrices)
				currentBalance += balance * price
			}
		}

		return currentBalance
	}
)

export const selectTotalBalanceHistory = createSelector(
	(state: RootState) => state.wallet.balanceSeries,
	(state: RootState) => state.prices.pricesSeries,
	selectTokens,
	(state: RootState) => state.wallet.networkId,
	selectTotalBalance,
	selectOraclePrices,
	(balanceSeries, priceSeries, tokenInfo, networkId, currentBalance, oraclePrices) => {
		// Calculate total balance history
		const balanceHistory: Record<number, number> = {}
		for (const [timestamp, balances] of Object.entries(balanceSeries)) {
			const timestampNum: number = Number(timestamp)
			balanceHistory[timestampNum] = 0
			for (const [token, balance] of Object.entries(balances)) {
				const network = networkId ?? DEFAULT_NETWORK_ID
				const info = tokenInfo.find(t=>t.address===token)
				if (info !== undefined) {
					const price = getPriceByOraclePrice(token, tokenInfo, network, priceSeries[timestampNum], oraclePrices)
					
					balanceHistory[timestampNum] += balance * price
				}
			}
		}

		// Covert to List
		const balanceHistoryList = Object.entries(balanceHistory)
			.sort((a, b)=>Number(a[0])-Number(b[0]))
			.map(e=>({timestamp: Number(e[0])*1000, total: e[1]}))

		const now = (new Date()).getTime()
		balanceHistoryList.push({timestamp: now, total: currentBalance})
		return balanceHistoryList
	}
)

export const selectUnrealizedBalance = createSelector(
	(state: RootState) => state.prices.onChainPrices,
	selectTokens,
	(state: RootState) => state.wallet.networkId,
	selectOpenOrders,
	selectOraclePrices,
	selectMarkets,
	(prices, tokenInfo, networkId, openOrders, oraclePrices, markets) => {
		// Calculate total balance currently
		let unrealizedBalance: number = 0
		
		for (const [marketAddress, orders] of Object.entries(openOrders)) {
			const market = markets.find(m=>m.marketAddress==marketAddress)
			if (market === undefined) continue
			for (const order of orders) {
				const baseToken = market.tokenX.address
				const info = tokenInfo.find(i=>i.address==baseToken)
				const network = networkId ?? DEFAULT_NETWORK_ID
				if (info !== undefined) {
					const price = getPriceByOraclePrice(baseToken, tokenInfo, network, prices, oraclePrices)
					unrealizedBalance += order.direction == OrderDirection.buy
						? order.earned * price
						: order.earned
				}
			}
		}

		return unrealizedBalance
	}
)